import { Routes } from '@angular/router';
import { PermissionEnum } from '@core/enums/permission.enum';
import { authGuard } from '@core/guards/auth.guard';
import { errorPageGuard } from '@core/guards/error-page.guard';
import { guestGuard } from '@core/guards/guest.guard';
import { hasPermissionsGuard } from '@core/guards/has-permissions.guard';
import { sessionUserPermissionsResolver } from '@core/resolvers/session-user-permissions.resolver';
import { sessionUserResolver } from '@core/resolvers/session-user.resolver';
import { userWorktimeResolver } from '@shared/resolvers/user-worktime.resolver';
import { ErrorPageComponent } from '@features/error-page/error-page.component';
import { LoginComponent } from '@features/login/login.component';

export const routes: Routes = [
  {
    path: '',
    redirectTo: 'dashboard',
    pathMatch: 'full',
  },
  {
    path: 'dashboard',
    loadChildren: () =>
      import('./features/dashboard/dashboard.routes').then(
        (r) => r.dashboardRoutes
      ),
    canMatch: [authGuard],
    resolve: {
      userPermissions: sessionUserPermissionsResolver
    }
  },
  {
    path: 'my-profile',
    loadComponent: () =>
      import('./features/my-profile/my-profile.component').then(
        (m) => m.MyProfileComponent
      ),
      canMatch: [authGuard, hasPermissionsGuard],
      data: {
        menuId: 'MY_PROFILE',
        breadcrumb: 'MY_PROFILE.BREADCRUMB',
        permissions: [
          PermissionEnum.MANAGE_MY_PROFILE
        ]
      }
  },
  {
    path: 'my-skills',
    loadComponent: () =>
      import('./features/my-skills/my-skills.component').then(
        (m) => m.MySkillsComponent
      ),
    canMatch: [authGuard, hasPermissionsGuard],
    resolve: {
      user: sessionUserResolver
    },
    data: {
      menuId: 'MY_SKILLS',
      breadcrumb: 'MY_SKILLS.BREADCRUMB',
      permissions: [
        PermissionEnum.MANAGE_MY_SKILLS
      ]
    },
  },
  {
    path: 'my-timesheets',
    loadComponent: () =>
      import('./features/my-timesheets/my-timesheets.component').then(
        (m) => m.MyTimesheetsComponent
      ),
    canMatch: [authGuard, hasPermissionsGuard],
    resolve: {
      user: sessionUserResolver,
      userPermissions: sessionUserPermissionsResolver,
      userWorkTime: userWorktimeResolver
    },
    data: {
      menuId: 'MY_TIMESHEETS',
      breadcrumb: 'TIMESHEET.BREADCRUMB',
      permissions: [
        PermissionEnum.MANAGE_MY_TIMESHEETS
      ]
    },
  },
  {
    path: 'my-reservations',
    loadChildren: () =>
      import('./features/my-reservations/my-reservations.routes').then(
        (r) => r.myReservationsRoutes
      ),
    canMatch: [authGuard, hasPermissionsGuard],
    data: {
      menuId: 'MY_RESERVATIONS',
      breadcrumb: 'MY_RESERVATIONS.BREADCRUMB',
      permissions: [
        PermissionEnum.MANAGE_MY_RESERVATIONS
      ]
    },
  },
  {
    path: 'my-absences',
    loadComponent: () =>
      import('./features/my-absences/my-absences.component').then(
        (m) => m.MyAbsencesComponent
      ),
    canMatch: [authGuard, hasPermissionsGuard],
    resolve: {
      user: sessionUserResolver
    },
    data: {
      menuId: 'MY_ABSENCES',
      breadcrumb: 'MY_ABSENCES.BREADCRUMB',
      permissions: [
        PermissionEnum.MANAGE_MY_ABSENCES
      ]
    },
  },
  {
    path: 'my-cv',
    loadComponent: () =>
      import('./features/my-cv/my-cv.component').then(
        (m) => m.MyCvComponent
      ),
    canMatch: [authGuard, hasPermissionsGuard],
    data: {
      menuId: 'MY_CV',
      breadcrumb: 'MY_CV.LABEL',
      permissions: [
        PermissionEnum.MANAGE_MY_CV
      ]
    },
  },
  {
    path: 'my-overtimes',
    loadComponent: () =>
      import('./features/my-overtimes/my-overtimes.component').then(
        (m) => m.MyOvertimesComponent
      ),
    canMatch: [authGuard, hasPermissionsGuard],
    resolve: {
      user: sessionUserResolver
    },
    data: {
      menuId: 'MY_OVERTIMES',
      breadcrumb: 'MY_OVERTIMES.BREADCRUMB',
      permissions: [
        PermissionEnum.MANAGE_MY_OVERTIMES
      ]
    }
  },
  {
    path: 'management-features',
    loadChildren: () =>
      import('./features/management-features/management-features.routes').then(
        (r) => r.ManagementFeaturesRoutes
      ),
    canMatch: [authGuard, hasPermissionsGuard],
    data: {
      menuId: 'MANAGEMENT_FEATURES',
      breadcrumb: 'MANAGEMENT_FEATURES.BREADCRUMB',
      atLeastPermissions: [
        PermissionEnum.MANAGE_ROLE_PERMISSIONS,
        PermissionEnum.VIEW_ABSENCES,
        PermissionEnum.VIEW_MAPPING_SKILLS,
        PermissionEnum.VIEW_ORDER_ACTIVATIONS,
        PermissionEnum.VIEW_OVERTIMES,
        PermissionEnum.VIEW_RESERVATIONS,
        PermissionEnum.VIEW_SKILLS_REGISTRY,
        PermissionEnum.VIEW_TIMESHEETS,
        PermissionEnum.VIEW_USERS,
        PermissionEnum.VIEW_FINANCE_OVERVIEW,
        PermissionEnum.VIEW_SUPPLIERS,
        PermissionEnum.VIEW_JOB_OFFER,
        PermissionEnum.MANAGE_FUNDED_TIMESHEETS,
        PermissionEnum.MANAGE_STRATEGIC_PROCESS
      ]
    }
  },
  {
    path: 'login',
    component: LoginComponent,
    canMatch: [guestGuard]
  },
  {
    path: 'error-page',
    component: ErrorPageComponent,
    canMatch: [errorPageGuard]
  },
  {
    // Unknown URLs are a true 404 for anyone: the page renders directly (defaulting to
    // NOT_FOUND) and never hits the error-page guard, which requires an errorCode.
    path: '**',
    component: ErrorPageComponent
  }
];
